import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import Page from "@/components/layout/Page";
import { ProductModal } from "@/components/ProductModal";
import { ProductQuickView } from "@/components/ProductQuickView";
import { ProductImagePlaceholder } from "@/components/ProductImagePlaceholder";
import { products, type Product } from "@/data/products";
import { SEOHead } from "@/components/seo/SEOHead";
import { BreadcrumbSchema } from "@/components/seo/StructuredData";
import { useCart } from "@/contexts/CartContext";
import { Eye, Sparkles, Users, ShoppingBag, CheckCircle2 } from "lucide-react";
import { isBundleEligible } from "@/utils/bundlePricing";

// Format price for display
function formatPrice(price: number): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
  }).format(price);
}

// Product categorization with placement field support
const isTeamsAndSchoolsProduct = (product: Product): boolean => {
  // Explicit placement takes priority
  if (product.placement === 'teams' || product.placement === 'both') return true;
  if (product.placement === 'shop') return false;
  
  const name = product.name.toLowerCase();
  // Education Kits, high-value bundles (≥250), bulk sets
  return (
    name.includes('education kit') ||
    product.price >= 250 ||
    name.includes('set of 5')
  );
};

const isShopProduct = (product: Product): boolean => {
  // Explicit placement takes priority
  if (product.placement === 'shop' || product.placement === 'both') return true;
  if (product.placement === 'teams') return false;
  
  return !isTeamsAndSchoolsProduct(product);
};

type TabType = 'shop' | 'teams';

export default function Shop() {
  const { toast } = useToast();
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('shop');
  const { addItem } = useCart();

  // Filter and sort products based on active tab
  const filteredProducts = useMemo(() => {
    const filtered = activeTab === 'shop' 
      ? products.filter(isShopProduct)
      : products.filter(isTeamsAndSchoolsProduct);
    
    return [...filtered].sort((a, b) => b.price - a.price);
  }, [activeTab]);

  const handleAddToCart = (product: Product, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    addItem({
      id: product.slug,
      name: product.name,
      slug: product.slug,
      price: product.price,
      image: product.image?.src,
    });
    toast({
      title: "Added to cart",
      description: `${product.name} has been added to your cart.`,
    });
  };

  const handleQuickView = (product: Product, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedProduct(product);
  };
  
  return (
    <>
      <SEOHead
        title="Shop Sensory Tools"
        description="Shop premium tactile sensory boards, Library Squares, and Sensory Pads. Handmade in the UK for ADHD, autism, anxiety, and focus support."
        canonical="/shop"
        keywords="buy sensory boards, sensory tools shop, ADHD tools, autism sensory products, tactile learning tools, focus tools for office"
      />
      <BreadcrumbSchema items={[
        { name: "Home", url: "/" },
        { name: "Shop", url: "/shop" }
      ]} />
      
      <Page>
      <section className="min-h-screen w-full">
        <div className="pb-24 flex flex-col gap-8">
        <header className="space-y-3 text-center md:text-left">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-emerald-300/80">
            Shop
          </p>
          <h1 className="text-3xl font-semibold tracking-tight page-title-gradient md:text-4xl">
            Choose the right Samboards tools for your space
          </h1>
          <p className="max-w-2xl text-sm text-slate-300/80">
            From full-size Samboards to portable Mini boards and specialist
            ranges, every product is designed to support autistic, ADHD and
            neurodivergent people in homes, schools and clinical settings.
          </p>
        </header>

        {/* Tab Navigation */}
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
          <button
            onClick={() => setActiveTab('shop')}
            className={`flex items-center justify-center gap-2 px-6 py-3 rounded-full font-medium text-sm transition-all duration-200 ${
              activeTab === 'shop'
                ? 'bg-gradient-to-r from-emerald-400 to-cyan-400 text-slate-900 shadow-[0_0_30px_rgba(45,255,196,0.3)]'
                : 'bg-slate-800/60 text-slate-300 hover:bg-slate-800 hover:text-emerald-200 border border-white/10'
            }`}
          >
            <ShoppingBag className="w-4 h-4" />
            Shop
          </button>
          <button
            onClick={() => setActiveTab('teams')}
            className={`flex items-center justify-center gap-2 px-6 py-3 rounded-full font-medium text-sm transition-all duration-200 ${
              activeTab === 'teams'
                ? 'bg-gradient-to-r from-emerald-400 to-cyan-400 text-slate-900 shadow-[0_0_30px_rgba(45,255,196,0.3)]'
                : 'bg-slate-800/60 text-slate-300 hover:bg-slate-800 hover:text-emerald-200 border border-white/10'
            }`}
          >
            <Users className="w-4 h-4" />
            Teams & Schools
          </button>
        </div>

        {/* Teams & Schools Header */}
        {activeTab === 'teams' && (
          <div className="rounded-2xl bg-gradient-to-br from-slate-800/80 via-slate-800/60 to-slate-900/80 border border-white/10 p-6 md:p-8 space-y-5">
            <div className="space-y-2">
              <h2 className="text-xl md:text-2xl font-semibold text-emerald-50">
                Tools designed for classrooms, services, and shared spaces
              </h2>
              <p className="text-sm md:text-base text-slate-300/80 max-w-2xl">
                Scalable sensory solutions for schools, teams, and professional environments.
              </p>
            </div>
            <div className="flex flex-wrap gap-4 text-sm text-slate-400">
              <span className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                Volume pricing built in
              </span>
              <span className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                Designed for repeated daily use
              </span>
              <span className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                Suitable for schools, services, and organisations
              </span>
            </div>
          </div>
        )}

        {/* Multi-buy Offer Banner - Only show on Shop tab */}
        {activeTab === 'shop' && (
          <div className="rounded-xl bg-gradient-to-r from-emerald-500/10 via-teal-500/10 to-emerald-500/10 border border-emerald-500/30 px-4 py-3 sm:px-6 sm:py-4">
            <div className="flex items-center gap-3 justify-center text-center">
              <Sparkles className="h-5 w-5 text-emerald-400 flex-shrink-0" />
              <p className="text-sm sm:text-base text-emerald-200">
                <span className="font-semibold">Multi-buy offer:</span>{' '}
                <span className="text-emerald-300">2 for £10 • 3 for £12.95</span>{' '}
                <span className="text-slate-300">(Library Squares)</span>
                <span className="hidden sm:inline text-slate-400 ml-1">— Discount applies automatically at checkout</span>
              </p>
            </div>
            <p className="text-xs text-slate-400 text-center mt-1 sm:hidden">
              Discount applies automatically at checkout
            </p>
          </div>
        )}

        {/* Products Grid */}
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {filteredProducts.map((product) => {
            const displayImage = product.image;
            const isAvailable = !product.outOfStock;
            
            return (
              <Link
                key={product.slug}
                to={product.slug}
                className="group relative flex flex-col h-full rounded-3xl border border-white/5 bg-slate-900/70 shadow-[0_18px_60px_rgba(0,0,0,0.65)] backdrop-blur-xl interactive-card hover:border-emerald-500/30 transition-colors"
              >
                {/* Eco Badge for Agri products */}
                {product.badge && (
                  <span className="product-badge">
                    {product.badge}
                  </span>
                )}
                {!isAvailable && (
                  <span className="absolute top-3 left-3 z-20 inline-flex items-center rounded-full border border-slate-400/50 bg-slate-500/20 px-3 py-1 text-[11px] font-medium uppercase tracking-[0.15em] text-slate-300 backdrop-blur-sm">
                    Out of stock
                  </span>
                )}
                
                {/* Quick View Tooltip */}
                {product.features && product.features.length > 0 && (
                  <div className="absolute top-3 right-3 z-10 pointer-events-none">
                    <ProductQuickView
                      features={product.features}
                      productName={product.name}
                    />
                  </div>
                )}
                
                <div className="w-full h-[220px] md:h-[260px] lg:h-[300px] overflow-hidden rounded-t-3xl product-card-image-container">
                  {displayImage ? (
                    <div className="w-full h-full bg-gradient-to-br from-slate-800/60 to-slate-900/80 p-6">
                      <img 
                        src={displayImage.src} 
                        alt={displayImage.alt}
                        className="w-full h-full object-contain transition-transform duration-300 group-hover:scale-105"
                        style={{ 
                          imageRendering: '-webkit-optimize-contrast',
                          backfaceVisibility: 'hidden',
                          transform: 'translateZ(0)'
                        }}
                      />
                    </div>
                  ) : (
                    <ProductImagePlaceholder className="w-full h-full" aspectRatio="" />
                  )}
                </div>
                
                <div className="flex flex-col flex-1 px-5 py-6">
                  <div className="space-y-3 flex-1">
                    <h2 className="text-xl font-semibold text-emerald-50 leading-tight group-hover:text-emerald-200 transition-colors">
                      {product.name}
                    </h2>
                    <p className={`text-sm leading-relaxed ${!product.description ? "text-slate-400/60 italic" : "text-slate-200/80"} line-clamp-3`}>
                      {product.description || "Description coming soon"}
                    </p>
                  </div>

                  <div className="mt-6 flex flex-col gap-3">
                    {product.price > 0 && (
                      <div className="flex flex-col gap-1.5">
                        <div className="flex items-center gap-2">
                          <span className="text-2xl font-bold text-emerald-300">
                            {formatPrice(product.price)}
                          </span>
                          {activeTab === 'shop' && isBundleEligible(product.price, product.name) && (
                            <span className="inline-flex items-center rounded-full bg-emerald-500/20 border border-emerald-500/40 px-2 py-0.5 text-[10px] sm:text-xs font-medium text-emerald-300 whitespace-nowrap">
                              2 for £10 / 3 for £12.95
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                    
                    <div className="flex flex-col xs:flex-row gap-2">
                      <Button
                        onClick={(e) => handleAddToCart(product, e)}
                        className="w-full rounded-full bg-gradient-to-r from-emerald-300 to-cyan-300 text-slate-900 font-medium shadow-[0_0_40px_rgba(45,255,196,0.35)] interactive-button disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none py-3 text-base"
                        disabled={!isAvailable}
                      >
                        {!isAvailable ? 'Out of stock' : 'Add to cart'}
                      </Button>
                      <button
                        type="button"
                        onClick={(e) => handleQuickView(product, e)}
                        className="w-full rounded-full border-2 border-emerald-300/50 bg-transparent text-emerald-300 hover:bg-emerald-300/10 hover:text-emerald-200 hover:border-emerald-300 interactive-glow px-4 py-3 text-base font-semibold transition-all duration-200 cursor-pointer flex items-center justify-center gap-2"
                      >
                        <Eye className="w-4 h-4" />
                        Quick view
                      </button>
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}

          {/* Access to Work CTA Card - Only show on Shop tab */}
          {activeTab === 'shop' && (
            <article className="group relative flex flex-col h-full rounded-3xl border border-emerald-300/30 bg-gradient-to-br from-emerald-900/20 to-slate-900/70 shadow-[0_18px_60px_rgba(0,0,0,0.65)] backdrop-blur-xl interactive-card">
              <div className="flex flex-col flex-1 px-5 py-6">
                <div className="space-y-3 flex-1">
                  <span className="inline-flex items-center rounded-full border border-emerald-300/50 bg-emerald-300/10 px-3 py-1 text-[11px] font-medium uppercase tracking-[0.15em] text-emerald-200">
                    Workplace Support
                  </span>
                  <h2 className="text-xl font-semibold text-emerald-50 leading-tight">
                    Access to Work Kits
                  </h2>
                  <p className="text-sm leading-relaxed text-slate-200/80">
                    Complete sensory support packages eligible for Access to Work funding. Perfect for neurodivergent employees seeking workplace adjustments.
                  </p>
                </div>

                <div className="mt-6">
                  <Link
                    to="/access-to-work-kits"
                    className="inline-flex w-full items-center justify-center rounded-full bg-gradient-to-r from-emerald-300 to-cyan-300 px-4 py-2.5 text-sm font-medium text-slate-900 shadow-[0_0_40px_rgba(45,255,196,0.35)] interactive-button"
                  >
                    Learn more
                  </Link>
                </div>
              </div>
            </article>
          )}
        </div>
        </div>
      </section>

      <ProductModal
        isOpen={selectedProduct !== null}
        onClose={() => setSelectedProduct(null)}
        product={selectedProduct || {
          name: "",
          price: 0,
          description: ""
        }}
        onAddToCart={() => {
          if (selectedProduct) {
            addItem({
              id: selectedProduct.slug,
              name: selectedProduct.name,
              slug: selectedProduct.slug,
              price: selectedProduct.price,
              image: selectedProduct.image?.src,
            });
            toast({
              title: "Added to cart",
              description: `${selectedProduct.name} has been added to your cart.`,
            });
          }
        }}
      />
    </Page>
    </>
  );
}
